import React, { useState } from 'react';
import { XIcon, PlusIcon, TrashIcon } from 'lucide-react';
interface AddInventoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (inventoryData: any) => void;
}
export function AddInventoryModal({
  isOpen,
  onClose,
  onSave
}: AddInventoryModalProps) {
  const [roomType, setRoomType] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [availableRooms, setAvailableRooms] = useState('');
  const [closedToArrivalDates, setClosedToArrivalDates] = useState<string[]>([]);
  const [closedToDepartureDates, setClosedToDepartureDates] = useState<string[]>([]);
  const [blackoutDates, setBlackoutDates] = useState<string[]>([]);
  const [newClosedToArrivalDate, setNewClosedToArrivalDate] = useState('');
  const [newClosedToDepartureDate, setNewClosedToDepartureDate] = useState('');
  const [newBlackoutDate, setNewBlackoutDate] = useState('');
  if (!isOpen) return null;
  const handleAddClosedToArrivalDate = () => {
    if (newClosedToArrivalDate && !closedToArrivalDates.includes(newClosedToArrivalDate)) {
      setClosedToArrivalDates([...closedToArrivalDates, newClosedToArrivalDate]);
      setNewClosedToArrivalDate('');
    }
  };
  const handleAddClosedToDepartureDate = () => {
    if (newClosedToDepartureDate && !closedToDepartureDates.includes(newClosedToDepartureDate)) {
      setClosedToDepartureDates([...closedToDepartureDates, newClosedToDepartureDate]);
      setNewClosedToDepartureDate('');
    }
  };
  const handleAddBlackoutDate = () => {
    if (newBlackoutDate && !blackoutDates.includes(newBlackoutDate)) {
      setBlackoutDates([...blackoutDates, newBlackoutDate]);
      setNewBlackoutDate('');
    }
  };
  const handleRemoveClosedToArrivalDate = (date: string) => {
    setClosedToArrivalDates(closedToArrivalDates.filter(d => d !== date));
  };
  const handleRemoveClosedToDepartureDate = (date: string) => {
    setClosedToDepartureDates(closedToDepartureDates.filter(d => d !== date));
  };
  const handleRemoveBlackoutDate = (date: string) => {
    setBlackoutDates(blackoutDates.filter(d => d !== date));
  };
  const handleSubmit = () => {
    const inventoryData = {
      roomType,
      fromDate,
      toDate,
      availableRooms: parseInt(availableRooms, 10) || 0,
      closedToArrivalDates,
      closedToDepartureDates,
      blackoutDates
    };
    onSave(inventoryData);
    onClose();
  };
  return <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-3103">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-3104">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true" data-id="element-3105">
          <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-3106"></div>
        </div>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-3107">
          <div className="flex justify-between items-center border-b border-gray-200 px-6 py-4" data-id="element-3108">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-3109">Add Inventory</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500" data-id="element-3110">
              <XIcon size={20} data-id="element-3111" />
            </button>
          </div>
          <div className="p-6" data-id="element-3112">
            <p className="text-sm text-gray-500 mb-4" data-id="element-3113">
              Select the room type and applicable inventory rules
            </p>
            <div className="space-y-4" data-id="element-3114">
              {/* Room Type */}
              <div data-id="element-3115">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3116">
                  Room Type*
                </label>
                <div className="relative" data-id="element-3117">
                  <select className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 appearance-none focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={roomType} onChange={e => setRoomType(e.target.value)} data-id="element-3118">
                    <option value="" data-id="element-3119">Select room type...</option>
                    <option value="premium-overwater" data-id="element-3120">
                      Premium Overwater Villa
                    </option>
                    <option value="water-villa" data-id="element-3121">Water Villa</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-3122">
                    <ChevronDownIcon size={16} className="text-gray-400" data-id="element-3123" />
                  </div>
                </div>
              </div>
              {/* From Date */}
              <div data-id="element-3124">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3125">
                  From*
                </label>
                <input type="text" placeholder="MM/DD/YYYY" className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={fromDate} onChange={e => setFromDate(e.target.value)} data-id="element-3126" />
              </div>
              {/* To Date */}
              <div data-id="element-3127">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3128">
                  To*
                </label>
                <input type="text" placeholder="MM/DD/YYYY" className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={toDate} onChange={e => setToDate(e.target.value)} data-id="element-3129" />
              </div>
              {/* Available Rooms */}
              <div data-id="element-3130">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3131">
                  Daily Available Rooms*
                </label>
                <input type="number" placeholder="Enter daily available rooms" className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={availableRooms} onChange={e => setAvailableRooms(e.target.value)} data-id="element-3132" />
              </div>
              {/* Closed to Arrival dates */}
              <div data-id="element-3133">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3134">
                  Closed to Arrival dates in this period
                </label>
                <div className="space-y-2" data-id="element-3135">
                  {closedToArrivalDates.map((date, index) => <div key={index} className="flex items-center" data-id="element-3136">
                      <input type="text" value={date} readOnly className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50" data-id="element-3137" />
                      <button onClick={() => handleRemoveClosedToArrivalDate(date)} className="ml-2 text-red-500" data-id="element-3138">
                        <TrashIcon size={16} data-id="element-3139" />
                      </button>
                    </div>)}
                  <div className="flex items-center" data-id="element-3140">
                    <input type="text" placeholder="MM/DD/YYYY" className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={newClosedToArrivalDate} onChange={e => setNewClosedToArrivalDate(e.target.value)} data-id="element-3141" />
                    <button onClick={handleAddClosedToArrivalDate} className="ml-2 text-red-500" data-id="element-3142">
                      <TrashIcon size={16} data-id="element-3143" />
                    </button>
                  </div>
                  <button onClick={handleAddClosedToArrivalDate} className="flex items-center text-red-600 text-sm font-medium" data-id="element-3144">
                    <PlusIcon size={16} className="mr-1" data-id="element-3145" />
                    Add
                  </button>
                </div>
              </div>
              {/* Closed to Departure dates */}
              <div data-id="element-3146">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3147">
                  Closed to Departure dates in this period
                </label>
                <div className="space-y-2" data-id="element-3148">
                  {closedToDepartureDates.map((date, index) => <div key={index} className="flex items-center" data-id="element-3149">
                      <input type="text" value={date} readOnly className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50" data-id="element-3150" />
                      <button onClick={() => handleRemoveClosedToDepartureDate(date)} className="ml-2 text-red-500" data-id="element-3151">
                        <TrashIcon size={16} data-id="element-3152" />
                      </button>
                    </div>)}
                  <div className="flex items-center" data-id="element-3153">
                    <input type="text" placeholder="MM/DD/YYYY" className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={newClosedToDepartureDate} onChange={e => setNewClosedToDepartureDate(e.target.value)} data-id="element-3154" />
                    <button onClick={handleRemoveClosedToDepartureDate} className="ml-2 text-red-500" data-id="element-3155">
                      <TrashIcon size={16} data-id="element-3156" />
                    </button>
                  </div>
                  <button onClick={handleAddClosedToDepartureDate} className="flex items-center text-red-600 text-sm font-medium" data-id="element-3157">
                    <PlusIcon size={16} className="mr-1" data-id="element-3158" />
                    Add
                  </button>
                </div>
              </div>
              {/* Blackout dates */}
              <div data-id="element-3159">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3160">
                  Blackout dates in this period
                </label>
                <div className="space-y-2" data-id="element-3161">
                  {blackoutDates.map((date, index) => <div key={index} className="flex items-center" data-id="element-3162">
                      <input type="text" value={date} readOnly className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50" data-id="element-3163" />
                      <button onClick={() => handleRemoveBlackoutDate(date)} className="ml-2 text-red-500" data-id="element-3164">
                        <TrashIcon size={16} data-id="element-3165" />
                      </button>
                    </div>)}
                  <div className="flex items-center" data-id="element-3166">
                    <input type="text" placeholder="MM/DD/YYYY" className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={newBlackoutDate} onChange={e => setNewBlackoutDate(e.target.value)} data-id="element-3167" />
                    <button onClick={handleRemoveBlackoutDate} className="ml-2 text-red-500" data-id="element-3168">
                      <TrashIcon size={16} data-id="element-3169" />
                    </button>
                  </div>
                  <button onClick={handleAddBlackoutDate} className="flex items-center text-red-600 text-sm font-medium" data-id="element-3170">
                    <PlusIcon size={16} className="mr-1" data-id="element-3171" />
                    Add
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-6 py-4 flex justify-end" data-id="element-3172">
            <button onClick={handleSubmit} className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-3173">
              Create
            </button>
          </div>
        </div>
      </div>
    </div>;
}
// Helper component for the ChevronDown icon
function ChevronDownIcon({
  size = 24,
  className = ''
}) {
  return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-3174">
      <polyline points="6 9 12 15 18 9" data-id="element-3175"></polyline>
    </svg>;
}